<template>
  <table
    :class="[
      'simpleTable',
      ...variants.map(variant => `simpleTable--${variant}`),
      { 'simpleTable--blog': blog },
    ]"
    ref="table"
  >
    <thead class="simpleTable__head" v-if="data.headings && data.headings.length">
      <tr class="simpleTable__headRow">
        <th
          scope="col"
          :class="[
            'simpleTable__headItem',
            index === 0 && !variants.includes('smallHeadings') ? 'simpleTable__headItem--bold' : '',
          ]"
          v-for="(item, index) in data.headings"
          :key="index"
        >
          {{ item }}
        </th>
      </tr>
    </thead>
    <tbody class="simpleTable__body">
      <tr class="simpleTable__bodyRow" v-for="(item, index) in data.content" :key="`row-${index}`">
        <td
          class="simpleTable__bodyItem"
          v-for="(element, subIndex) in item"
          :key="`item-${index}-${subIndex}`"
          :data-label="data.headings[subIndex]"
        >
          <div v-html="element" />
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
export default {
  name: 'SimpleTable',
  props: {
    data: {
      type: Object,
      default: () => {},
    },
    variants: {
      type: Array,
      default: () => [],
    },
    blog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      copyButton: null,
    };
  },
  mounted() {
    if (this.$refs.table) {
      setTimeout(() => {
        this.copyButton = this.$refs.table.querySelector('.simpleTable__copyButton');
        if (this.copyButton) {
          this.copyButton.addEventListener('click', this.copyHandler);
        }
      }, 1);
    }
  },
  destroyed() {
    if (this.copyButton) {
      this.copyButton.removeEventListener('click', this.copyHandler);
    }
  },
  methods: {
    copyHandler(e) {
      const element = e.currentTarget;
      const parentElement = element.parentElement;
      const tooltip = document.createElement('div');
      tooltip.innerHTML = 'Skopiowano!';
      tooltip.classList.add('simpleTable__copyTooltip');
      element.classList.add('simpleTable__copyButton--disabled');
      const text = parentElement.childNodes[0].textContent;

      if (!text) return;

      navigator.clipboard
        .writeText(text)
        .then(() => {
          if (parentElement) {
            parentElement.appendChild(tooltip);
            setTimeout(() => {
              tooltip.classList.add('simpleTable__copyTooltip--hidden');
              setTimeout(() => {
                tooltip.remove();
                element.classList.remove('simpleTable__copyButton--disabled');
              }, 500);
            }, 5000);
          }
        })
        .catch();
    },
  },
};
</script>

<style scoped lang="scss">
.simpleTable {
  $root: &;
  width: 100%;
  font-size: 1.6rem;
  line-height: 2rem;
  font-weight: 600;
  border: 1px solid $cAddLight;

  @include rwd('large-phone') {
    font-size: 1.4rem;
  }

  &__head {
    @include rwd('tablet') {
      position: absolute;
      margin: -1px;
      padding: 0;
      width: 1px;
      height: 1px;
      clip: rect(0 0 0 0);
      border: none;
      overflow: hidden;
    }
  }

  &__headItem,
  &__bodyItem {
    text-align: left;

    &:not(:first-of-type) {
      font-weight: 400;
    }
  }

  &__headItem {
    padding: 1.4rem 2rem 1.4rem 2rem;
    font-size: 1.2rem;
    line-height: 1.5rem;
    color: $cAddDark;

    &--bold {
      font-size: 1.6rem;
      font-weight: 700;
      line-height: 2rem;
      color: $cMainDark;
    }
  }

  &__bodyRow {
    @include rwd('tablet') {
      display: block;
      margin-bottom: 3.5rem;
      border-bottom: 3px solid #ddd;
    }
  }

  &__bodyItem {
    padding: 2.2rem 2rem 2.2rem 2rem;

    &:not(:first-of-type) {
      color: $cMainLight;
    }

    @include rwd('tablet') {
      display: flex;
      flex-direction: column;
      padding: 1.2rem 1rem;
      border-bottom: 1px solid #ddd;
      text-align: left;
      font-weight: 400;
      color: $cMainLight;
    }

    &::before {
      @include rwd('tablet') {
        content: attr(data-label);
        flex: 0 0 100%;
        max-width: 100%;
        font-weight: 600;
        color: $cMainDark;
      }
    }

    &:last-child {
      @include rwd('tablet') {
        border-bottom: 0;
      }
    }

    > div {
      @include rwd('tablet') {
        margin-top: 0.5rem;
        flex: 0 0 100%;
        max-width: 100%;
      }
    }

    ::v-deep {
      #{$root}__copy {
        position: relative;
        margin: 1.5rem 0;
        padding: 1.5rem 6.5rem 1.5rem 2rem;
        border: 1px solid $cAddLight;
        border-radius: 4px;
      }

      #{$root}__copyButton {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        right: 5px;
        top: 50%;
        transform: translateY(-50%);
        width: 4rem;
        height: 4rem;
        border-radius: 4px;
        background-color: $cSec;
        transition: background-color $tr;

        &--disabled {
          pointer-events: none;
          background-color: lighten($cSec, 10%);
        }

        span {
          display: block;
          width: 2.5rem;
          height: 2.5rem;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='13.85' height='13.711'%3E%3Cg data-name='Group 4189' fill='%23fff'%3E%3Cpath data-name='Rectangle 3502' d='M5.35 1.5v7h7v-7h-7M3.85 0h10v10h-10Z'/%3E%3Cpath data-name='Path 11509' d='M11.368 13.711H.001V2.737H2.85v1.5H1.502v7.974h8.367v-1.217h1.5Z'/%3E%3C/g%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: center;
        }

        &:hover {
          background-color: $cSecHover;
        }
      }

      #{$root}__copyTooltip {
        position: absolute;
        padding: 15px 20px;
        bottom: calc(100% + 6px);
        right: 0;
        background-color: $cWhite;
        border: 1px solid $cAddLight;
        border-radius: 4px;
        box-shadow: 0 0 20px $cAddLight;
        z-index: 1;
        transition: opacity $tr;

        &--hidden {
          opacity: 0;
        }
      }

      a {
        color: $cMain;
        text-decoration: underline;
        transition: color $tr;

        &:hover {
          color: $cSec;
        }
      }
    }
  }

  &--borderless {
    #{$root}__headRow,
    #{$root}__bodyRow {
      border-bottom: 1px solid $cAddLight;

      @include rwd('tablet') {
        border-bottom-width: 3px;
      }
    }
  }

  &--borderInside {
    #{$root}__headItem,
    #{$root}__bodyItem {
      padding-left: 2.5rem;
      padding-right: 2.5rem;

      @include rwd('tablet') {
        padding-left: 0;
        padding-right: 0;
      }
    }

    #{$root}__headItem {
      border-right: 1px solid $cAddLight;

      @include rwd('tablet') {
        border-right: none;
      }
    }

    #{$root}__bodyItem {
      border-top: 1px solid $cAddLight;
      border-right: 1px solid $cAddLight;

      @include rwd('tablet') {
        border-top: none;
        border-right: none;
      }
    }
  }

  &--firstColumnFixed {
    #{$root}__headItem,
    #{$root}__bodyItem {
      &:first-of-type {
        width: 40%;

        @include rwd('tablet') {
          width: 100%;
        }
      }
    }
  }

  &--smallHeadings {
    #{$root}__headItem {
      font-size: 1.2rem;
    }
  }

  &--smallPaddings {
    #{$root}__headItem,
    #{$root}__bodyItem {
      padding: 1.2rem;

      @include rwd('tablet') {
        padding-left: 0;
      }
    }
  }

  &--sameFonts {
    #{$root}__bodyItem {
      color: $cMainLight;
      font-weight: 400;
    }
  }

  &--blog {
    #{$root}__head {
      @include rwd('tablet') {
        position: initial;
        margin: initial;
        padding: initial;
        border: initial;
        width: initial;
        height: initial;
        clip: initial;
        overflow: initial;
      }
    }

    #{$root}__headItem {
      @include rwd('tablet') {
        padding: 1.2rem;
      }
    }

    #{$root}__bodyRow {
      @include rwd('tablet') {
        display: revert;
        margin-bottom: 0;
        border-bottom: none;
      }
    }

    #{$root}__bodyItem {
      @include rwd('tablet') {
        display: revert;
        flex-direction: initial;
        padding: 1.2rem;
        text-align: initial;
        border-top: 1px solid $cAddLight;
        border-right: 1px solid $cAddLight;
      }

      @include rwd('small-tablet') {
        min-width: 30vw;
      }

      @include rwd('large-phone') {
        min-width: 40vw;
      }

      &::before {
        @include rwd('tablet') {
          display: none;
        }
      }

      &:last-child {
        @include rwd('tablet') {
          border-bottom: 1px solid #ddd;
        }
      }

      > div {
        @include rwd('tablet') {
          margin-top: initial;
          flex: initial;
          max-width: unset;
        }
      }
    }
  }
}
</style>
